import { format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { selectCompanyName } from '_store/order/selector';
import { selectAccountManagerDetails } from '_store/order/selector';

import ContactCard from 'components/atomic/ContactCard';
import Icon from 'components/atomic/Icon';

function sanitizePhoneNumber(rawNumber) {
  return `+1${rawNumber?.replace(/\D/g, '')}`;
}

const StyledContainer = styled.div`
  display: grid;
  gap: ${theme.spaces.smallX}px;
  padding-top: ${theme.spaces.regular}px;
  margin-bottom: ${theme.spaces.large2X}px;

  .item-1 {
    grid-column-start: 1;
    grid-column-end: 3;
    grid-row-start: 1;
    grid-row-end: 2;
  }

  .item-2 {
    grid-column-start: 1;
    grid-column-end: 3;
    grid-row-start: 2;
    grid-row-end: 3;
  }

  .item-3 {
    display: flex;
    justify-content: flex-end;
    grid-column-start: 3;
    grid-column-end: 4;
    grid-row-start: 1;
    grid-row-end: 2;
  }

  @media ${theme.devices.tablet} {
    display: block;

    .item-2 {
      margin-bottom: ${theme.spaces.large2X}px;
    }

    .item-3 {
      justify-content: flex-start;
    }
  }
`;

const StyledHeadContainer = styled.div`
  span {
    display: block;
    font-family: 'Inter';
    font-size: ${theme.fontSizes.medium}px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: -0.02em;
    text-align: left;
    color: ${theme.colors.gray.primary};
  }

  span:first-child {
    margin-bottom: ${theme.spaces.smallX}px;
    font-size: ${theme.fontSizes.large4X}px;
    line-height: 32px;
  }

  span:last-child {
    color: ${theme.colors.gray.secondary};
  }
`;

const StyledSubsectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spaces.regular}px;
  font-family: 'Inter';
  font-size: ${theme.fontSizes.small}px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.03em;
  text-align: left;
  color: ${theme.colors.gray.secondary};
`;

const StyledIconContainer = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-right: ${theme.spaces.smallX}px;
`;

const ContactsSection = () => {
  const location = useLocation();

  const companyName = useSelector(selectCompanyName);
  const accountManagerDetails = useSelector(selectAccountManagerDetails);

  const isSupport = location.pathname.includes('support');
  const isBookkeeping = location.pathname.includes('bookkeeping');
  const isTaxPreparation = location.pathname.includes('tax-preparation');
  const isDashboard = location.pathname.includes('overview') && !isBookkeeping;

  return (
    <StyledContainer>
      <div className="item-1">
        <StyledSubsectionTitle>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.home} color={theme.colors.gray.secondary} />
          </StyledIconContainer>
          Menu
        </StyledSubsectionTitle>
      </div>

      <div className="item-2">
        <StyledHeadContainer>
          {isDashboard && <span>Dashboard - Monexa</span>}
          {isBookkeeping && <span>Bookkeeping - Monexa</span>}
          {isTaxPreparation && <span>Tax Preparation - Monexa</span>}
          {isSupport && <span>Chat - Monexa</span>}
          <span>{companyName}</span>
          <span>{format(new Date(), 'eeee MMMM dd, yyyy')}</span>
        </StyledHeadContainer>
      </div>

      <div className="item-3">
        <StyledSubsectionTitle>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.home} color={theme.colors.gray.secondary} />
          </StyledIconContainer>
          Contact Your Team
        </StyledSubsectionTitle>
      </div>

      <div className="item-4">
        <ContactCard
          active
          name={accountManagerDetails?.name || 'N/A'}
          role="Account Manager"
          email={accountManagerDetails?.email || 'N/A'}
          phone={sanitizePhoneNumber(accountManagerDetails?.phone)}
        />
      </div>
    </StyledContainer>
  );
};

export default ContactsSection;
